# Postbacks

N4 notifies your `postbackUrl` whenever a payment transitions state.

## HMAC verification is OPTIONAL but recommended

Every postback ships with an `X-N4-Signature` header (HMAC-SHA256). Verifying it lets you
reject spoofed POSTs that might trick your backend into marking a fake transaction as paid.

**N4 does not enforce verification on your side** — if you ignore the header, postbacks
still flow normally. The header is a security primitive *we provide*; whether you use it is
your call. Code samples below cover Node, Python, and PHP — pick one and wire it in. The
signing secret is per-store and rotatable from the admin portal at any time (auto-bootstrapped
on first send; rotate via the **Lojas → detalhe → Assinatura de postback → ↻ Rotacionar**
button when you suspect compromise).

## Events

| Event | Fires when |
|---|---|
| `payment.paid` | Provider confirms the payment cleared |
| `payment.failed` | Provider rejected the payment |
| `payment.expired` | PIX/boleto window closed unpaid |
| `payment.processing` | Card flow hit `requires_action` (3DS, etc) |
| `payment.refunded` | Full or partial refund completed |
| `payment.reminder_due` | Unpaid PIX/boleto past store's reminder threshold (default OFF) |
| `transfer.completed` / `transfer.failed` | PIX-out (programmatic transfer) finalized |
| `payment.chargeback_opened` | Card chargeback opened by buyer's issuer — *opt-in via store setting `postback_chargeback_enabled`* |
| `payment.chargeback_won` | N4 defended the chargeback and provider ruled in merchant's favor |
| `payment.chargeback_lost` | Chargeback finalized against merchant — funds debited |
| `payment.pix_med_opened` | PIX MED (Mecanismo Especial de Devolução) opened by payer's bank — *opt-in via `postback_pix_med_enabled`* |
| `payment.pix_med_accepted` / `payment.pix_med_rejected` | Operator decision on the MED case |
| `payment.pix_med_reversed` | MED auto-executed by BACEN — funds already debited |

**Opt-in events:** chargeback and PIX MED events are OFF by default. Enable per store via admin
**Lojas → detalhe → Editar config → "Receber postbacks de disputa"**. Default-off preserves
backward compat with integrators that don't expect these payloads.

## Headers

Every postback carries:

```
Content-Type: application/json
X-N4-Event-Id: <uuid>          // The id of the delivery row (idempotency key on your side)
X-N4-Timestamp: <unix-ms>      // When N4 signed this payload
X-N4-Signature: sha256=<hex>   // HMAC-SHA256(secret, `${timestamp}.${rawBody}`)
X-N4-Signature-Previous: sha256=<hex>   // Only present during a 24h rotation grace window
```

Replay protection: reject signatures with `timestamp` older than 5 minutes.

### Signing secret rotation — dual-header grace period

When the operator rotates the postback signing secret, N4 ships **both** signatures for 24h:
`X-N4-Signature` (new secret) and `X-N4-Signature-Previous` (old secret). After 24h only the
new signature ships. Recommended verifier: accept the payload if **either** header validates
against the secret you have on file. That way rotation never breaks your endpoint mid-flight
— you have 24h to deploy the new secret on your side.

```javascript
function verifyN4Either(req, currentSecret, previousSecret = null) {
  // try current first
  if (verifyN4Signature(req, currentSecret, 'x-n4-signature')) return true;
  // during grace window, also accept previous
  if (previousSecret && verifyN4Signature(req, previousSecret, 'x-n4-signature-previous')) return true;
  return false;
}
```

If you don't rotate often, just keep verifying against `x-n4-signature` against the current
secret — single-header rejection still works correctly outside the grace window.

## Verifying — Node.js

```javascript
import crypto from 'node:crypto';

function verifyN4Signature(req, secret) {
  const timestamp = req.headers['x-n4-timestamp'];
  const signature = req.headers['x-n4-signature'];
  if (!timestamp || !signature) return false;

  const age = Date.now() - Number(timestamp);
  if (age < 0 || age > 5 * 60 * 1000) return false; // > 5min skew

  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(`${timestamp}.${req.rawBody.toString('utf8')}`)
    .digest('hex');

  return crypto.timingSafeEqual(Buffer.from(signature), Buffer.from(expected));
}
```

## Verifying — Python

```python
import hmac, hashlib, time

def verify_n4(body_bytes: bytes, headers: dict, secret: str) -> bool:
    ts = headers.get('x-n4-timestamp')
    sig = headers.get('x-n4-signature')
    if not ts or not sig:
        return False
    age_ms = time.time() * 1000 - int(ts)
    if age_ms < 0 or age_ms > 5 * 60 * 1000:
        return False
    expected = 'sha256=' + hmac.new(
        secret.encode('utf-8'),
        f"{ts}.{body_bytes.decode('utf-8')}".encode('utf-8'),
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(sig, expected)
```

## Verifying — PHP

```php
function verify_n4($raw_body, $headers, $secret) {
  $ts  = $headers['x-n4-timestamp'] ?? null;
  $sig = $headers['x-n4-signature'] ?? null;
  if (!$ts || !$sig) return false;

  $age_ms = (microtime(true) * 1000) - (int)$ts;
  if ($age_ms < 0 || $age_ms > 5 * 60 * 1000) return false;

  $expected = 'sha256=' . hash_hmac('sha256', "$ts.$raw_body", $secret);
  return hash_equals($sig, $expected);
}
```

## Payload shape

By default postbacks are **flat** — event type at top level, payment fields siblings:

```json
{
  "event": "payment.paid",
  "id": "delivery_abc...",
  "transactionId": "intent_xyz...",
  "externalRef": "order-42",
  "status": "paid",
  "paymentMethod": "credit_card",
  "amount": 9990,
  "paidAt": "2026-05-16T13:46:01.926Z",
  "metadata": {
    "n4PaymentIntentId": "intent_xyz...",
    "internalFee": {"amountCents": 50, "currency": "BRL"}
  }
}
```

When your store has `postback_status_dialect='elysium'`, N4 wraps in an envelope and translates the
status enum + field names to match the PrimeCash spec:

```json
{
  "id": "delivery_abc...",
  "type": "transaction",
  "objectId": "intent_xyz...",
  "url": "https://n4-payments.com.br/...",
  "data": { "status": "paid", "secureUrl": "...", "fee": {...}, /* ... */ }
}
```

## Retry schedule

Failed deliveries (non-2xx, network error, timeout) are retried explicitly:

- Attempt 2: +1 minute
- Attempt 3: +5 minutes
- Attempt 4: +15 minutes
- Attempt 5: +1 hour
- After attempt 5: `dead_letter` state. Manual resend via admin.

4xx other than 429 → terminal `failed` (no retry — your endpoint rejected the payload).

10-second HTTP timeout per attempt.
